import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '../../assets/icons/search.svg?react';
import { InterventionRoomPropertyControllerApi } from '../../api/generated';
import type { InterventionRoomPropertyResponse } from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { useAppDispatch } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { Table } from '../UI';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { DeleteInterventionRoomPropertyModal } from './DeleteInterventionRoomPropertyModal';
import { FooterCell } from './FooterCell';
import { getInterventionRoomPropertiesColumns } from './interventionRoomPropertiesColumns';
import styles from './InterventionRoomPropertiesSettings.module.css';

const NEW_PROPERTY_ID = 'new' as const;
type EditingPropertyId = string | null;

export const InterventionRoomPropertiesSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [properties, setProperties] = useState<InterventionRoomPropertyResponse[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPropertyId, setEditingPropertyId] = useState<EditingPropertyId>(null);
  const [editingName, setEditingName] = useState('');
  const [editingComment, setEditingComment] = useState('');
  const [propertyToDelete, setPropertyToDelete] = useState<InterventionRoomPropertyResponse | null>(
    null
  );

  const refreshProperties = useCallback(async () => {
    try {
      const config = createApiConfiguration();
      const api = new InterventionRoomPropertyControllerApi(config);
      const response = await api.listInterventionRoomProperties();
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to fetch intervention room properties:', error);
      dispatch(showError({ message: t('fetch-error') }));
    }
  }, [dispatch, t]);

  useEffect(() => {
    const init = async () => {
      await refreshProperties();
    };
    void init();
  }, [refreshProperties]);

  const filteredProperties = useMemo(() => {
    if (!searchQuery) return properties;
    const q = searchQuery.toLowerCase();
    return properties.filter((p) => p.name.toLowerCase().includes(q));
  }, [properties, searchQuery]);

  const effectiveEditingPropertyId: EditingPropertyId =
    editingPropertyId === null || editingPropertyId === NEW_PROPERTY_ID
      ? editingPropertyId
      : filteredProperties.some((p) => p.id === editingPropertyId)
        ? editingPropertyId
        : null;

  const startEdit = useCallback((property: InterventionRoomPropertyResponse) => {
    setEditingPropertyId(property.id);
    setEditingName(property.name);
    setEditingComment(property.description ?? '');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingPropertyId(null);
    setEditingName('');
    setEditingComment('');
  }, []);

  const saveEdit = useCallback(async () => {
    if (!effectiveEditingPropertyId || effectiveEditingPropertyId === NEW_PROPERTY_ID) return;
    const trimmedName = editingName.trim();
    if (trimmedName.length === 0) return;
    const propertyId = effectiveEditingPropertyId;
    try {
      const config = createApiConfiguration();
      const api = new InterventionRoomPropertyControllerApi(config);
      await api.updateInterventionRoomProperty({
        propertyId,
        updateInterventionRoomPropertyRequest: {
          name: trimmedName,
          description: editingComment || undefined
        }
      });
      setEditingPropertyId(null);
      setEditingName('');
      setEditingComment('');
      await refreshProperties();
    } catch (error) {
      console.error('Failed to update intervention room property:', error);
      dispatch(showError({ message: t('update-error') }));
    }
  }, [effectiveEditingPropertyId, editingName, editingComment, refreshProperties, dispatch, t]);

  const createProperty = useCallback(
    async (name: string) => {
      try {
        const config = createApiConfiguration();
        const api = new InterventionRoomPropertyControllerApi(config);
        await api.createInterventionRoomProperty({
          createInterventionRoomPropertyRequest: { name }
        });
        setEditingPropertyId(null);
        await refreshProperties();
      } catch (error) {
        console.error('Failed to create intervention room property:', error);
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [refreshProperties, dispatch, t]
  );

  const handleDeleted = useCallback(() => {
    setPropertyToDelete(null);
    void refreshProperties();
  }, [refreshProperties]);

  const columns = useMemo(
    () =>
      getInterventionRoomPropertiesColumns(t, {
        editingPropertyId: effectiveEditingPropertyId,
        editingName,
        editingComment,
        onStartEdit: startEdit,
        onNameChange: setEditingName,
        onCommentChange: setEditingComment,
        onSave: () => {
          void saveEdit();
        },
        onCancel: cancelEdit,
        onDelete: (property) => setPropertyToDelete(property)
      }),
    [t, effectiveEditingPropertyId, editingName, editingComment, startEdit, saveEdit, cancelEdit]
  );

  return (
    <section>
      <SectionHeader title={t('settings-intervention-room-properties-header')}>
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              {t('settings-search-intervention-room-property')}
            </label>
            <div className={styles.searchInputWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                value={searchQuery}
                placeholder={t('intervention-room-properties-search-placeholder')}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </SectionHeader>

      <Table
        columns={columns}
        data={filteredProperties}
        getRowKey={(property) => property.id}
        footer={
          <FooterCell
            isEditing={editingPropertyId === NEW_PROPERTY_ID}
            onStartEdit={() => {
              setEditingPropertyId(NEW_PROPERTY_ID);
              setEditingName('');
              setEditingComment('');
            }}
            onSave={(name) => void createProperty(name)}
            onCancel={cancelEdit}
          />
        }
      />

      {propertyToDelete && (
        <DeleteInterventionRoomPropertyModal
          property={propertyToDelete}
          onClose={() => setPropertyToDelete(null)}
          onDeleted={handleDeleted}
        />
      )}
    </section>
  );
};
